<template>
  <div class="topics-manager">
    <div v-if="!isChild" class="mb-4 space-y-4">
      <!-- Selected topics section -->
      <div>
        <s-label class="mb-1.5">{{ t('labels.selectedTopics') }}</s-label>
        <div v-if="selectedTopics.length > 0" class="flex flex-wrap gap-2">
          <s-badge v-for="topicId in selectedTopics" :key="topicId">
            <span class="select-none">{{ getTopicDescription(topicId) }}</span>
            <button
              @click="removeTopic(topicId)"
              class="-my-1 -ml-1 -mr-1.5 p-1 opacity-70 transition hover:opacity-100"
            >
              <s-icon name="close" size="16" />
            </button>
          </s-badge>
        </div>
        <s-badge v-else color="ghost" class="italic font-normal">
          {{ t('labels.noTopicsSelected') }}
        </s-badge>
      </div>

      <!-- Search section -->
      <div class="relative group">
        <s-input-field
          id="topicsFilterText"
          v-model="searchText"
          @input="expandParentNodes"
          :placeholder="t('placeholders.search')"
          inputClass="pr-10 focus:pr-4 truncate"
          autocomplete="off"
        />
        <s-icon
          name="magnify"
          class="absolute top-1/2 -translate-y-1/2 right-4 text-blue-500 opacity-70 group-focus-within:opacity-0 transition-all ease-out duration-150 z-10"
          size="20"
        />
      </div>
    </div>

    <s-card v-if="displayedTopics.length > 0" padding="none" class="mb-2">
      <div
        v-for="topic in displayedTopics"
        :key="topic.id"
        class="border-b border-gray-200 even:bg-gray-300/10 pr-2"
      >
        <div class="grid grid-cols-[2.25rem_auto] topic-item flex items-center gap-2">
          <button
            v-if="hasChildren(topic)"
            class="group flex items-center justify-center place-self-stretch border-r border-gray-400/15 hover:bg-gray-300/15"
            @click.prevent="toggleExpand(topic.id)"
          >
            <s-icon
              name="chevron-right"
              class="group-hover:opacity-100 group-hover:text-blue-500 transform transition-transform cursor-pointer"
              :class="{'rotate-90': isExpanded(topic.id)}"
            />
          </button>

          <label
            class="col-start-2 flex items-center gap-1 cursor-pointer transition hover:text-blue-500"
          >
            <s-checkbox
              :id="'topic-' + topic.id"
              :model-value="selectedTopics.includes(topic.id)"
              @update:model-value="toggleTopic(topic.id)"
            />
            <span class="leading-tight py-2 select-none">{{ topic.description }}</span>
          </label>
        </div>
        <div v-if="hasChildren(topic) && isExpanded(topic.id)" class="children-container ml-6">
          <!-- Pass both the child topics and the complete set -->
          <TaskTopicsManager
            :topics="getChildTopics(topic)"
            :all-topics="completeTopicSet"
            v-model="selectedTopics"
            is-child
          />
        </div>
      </div>
    </s-card>
    <s-placeholder v-else>{{ t('placeholders.noTopicsFound') }}</s-placeholder>
  </div>
</template>

<script setup lang="ts">
import {computed, onMounted, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import SPlaceholder from '../../design-system/SPlaceholder.vue';
import SLabel from '../../design-system/SLabel.vue';
import SCard from '../../design-system/SCard.vue';
import SBadge from '../../design-system/SBadge.vue';
import SIcon from '../../design-system/SIcon.vue';
import SInputField from '../../design-system/SInputField.vue';
import SCheckbox from '../../design-system/SCheckbox.vue';

const props = defineProps<{
  topics: TaskTopicDtoWithLineage[];
  allTopics?: TaskTopicDtoWithLineage[];
  isChild?: boolean;
}>();

const {t} = useI18n({
  inheritLocale: true,
  scope: 'local',
});

const searchText = ref('');

// Use allTopics if provided, otherwise use topics as the complete set
const completeTopicSet = computed(() => props.allTopics || props.topics);

// Helper function to check if a topic matches the search
const topicMatchesSearch = (topic: TaskTopicDtoWithLineage): boolean => {
  if (!searchText.value) return true;
  const searchLower = searchText.value.toLowerCase();
  return topic.description.toLowerCase().includes(searchLower);
};

// Helper function to check if a topic or any of its descendants match the search
const topicOrDescendantsMatchSearch = (
  topic: TaskTopicDtoWithLineage,
  visited = new Set<number>()
): boolean => {
  // Prevent infinite recursion
  if (visited.has(topic.id)) return false;
  visited.add(topic.id);

  // Check if current topic matches
  if (topicMatchesSearch(topic)) return true;

  // Check children recursively
  if (topic.childrenIds?.length) {
    const children = completeTopicSet.value.filter((t) => topic.childrenIds.includes(t.id));
    return children.some((child) => topicOrDescendantsMatchSearch(child, visited));
  }

  return false;
};

const getChildTopics = (topic: TaskTopicDtoWithLineage): TaskTopicDtoWithLineage[] => {
  if (!topic.childrenIds?.length) return [];

  const children = completeTopicSet.value.filter((t) => topic.childrenIds.includes(t.id));
  if (!searchText.value) return children;

  // Only return children that match the search or have matching descendants
  return children.filter((child) => topicOrDescendantsMatchSearch(child));
};

const displayedTopics = computed(() => {
  const baseTopics = props.isChild
    ? props.topics
    : props.topics.filter((topic) => topic.isRootNode);

  if (!searchText.value) return baseTopics;

  // Only return topics that match the search or have matching descendants
  return baseTopics.filter((topic) => topicOrDescendantsMatchSearch(topic));
});

// When searching, automatically expand nodes that have matching descendants
watch(searchText, () => {
  if (!searchText.value) return;

  const searchLower = searchText.value.toLowerCase();
  completeTopicSet.value.forEach((topic) => {
    if (topicOrDescendantsMatchSearch(topic)) {
      // If this topic has matching descendants, expand it
      expandedNodes.value.add(topic.id);
    }
  });
});

const selectedTopics = defineModel<number[]>({
  default: [],
});

const expandedNodes = ref<Set<number>>(new Set());

const expandParentNodes = () => {
  selectedTopics.value.forEach((topicId) => {
    // Find all parent topics that have this topic in their children
    completeTopicSet.value.forEach((topic) => {
      if (topic.childrenIds?.includes(topicId)) {
        expandedNodes.value.add(topic.id);
      }
    });
  });
};

// Expand parent nodes on mount
onMounted(() => {
  expandParentNodes();
});

const toggleExpand = (topicId: number) => {
  if (expandedNodes.value.has(topicId)) {
    expandedNodes.value.delete(topicId);
  } else {
    expandedNodes.value.add(topicId);
  }
};

const hasChildren = (topic: TaskTopicDtoWithLineage) =>
  topic.childrenIds && topic.childrenIds.length > 0;

const isExpanded = (topicId: number) => expandedNodes.value.has(topicId);

const getTopicDescription = (topicId: number): string => {
  const topic = completeTopicSet.value.find((t) => t.id === topicId);
  return topic?.description || '';
};

const toggleTopic = (topicId: number) => {
  const index = selectedTopics.value.indexOf(topicId);
  if (index === -1) {
    selectedTopics.value.push(topicId);
  } else {
    selectedTopics.value.splice(index, 1);
  }
};

const removeTopic = (topicId: number) => {
  const index = selectedTopics.value.indexOf(topicId);
  if (index !== -1) {
    selectedTopics.value.splice(index, 1);
  }
};
</script>
<i18n>
{
  "en": {
    "labels.selectedTopics": "Selected Topics",
    "labels.noTopicsSelected": "No topics selected",
    "placeholders.search": "Search Topics",
    "placeholders.noTopicsFound": "No topics found"
  },
  "fr": {
    "labels.selectedTopics": "Sujets sélectionnés",
    "labels.noTopicsSelected": "Aucun sujet sélectionné",
    "placeholders.search": "Rechercher des sujets",
    "placeholders.noTopicsFound": "Aucun sujet trouvé"
  }
}
</i18n>
